// Kanban lane grouping for the Action Register (2026-08-06 design §3.1).
// Pure + exported so it can be unit-tested without React.
import type { ClientStatus, ProjectAction } from "../../lib/brain-api";

export const CLIENT_STATUS_LANES: ClientStatus[] = [
  "With Aeronet",
  "With Client",
  "In Review",
  "Approved",
  "Awaiting Customer",
  "Done",
  "Blocked",
];

export type ActionGroupBy = "clientStatus" | "assignee";

/** key is what a drop writes back (null = clear the field); label is what the lane shows. */
export interface ActionLane {
  key: string | null;
  label: string;
  rows: ProjectAction[];
}

export function groupActions(rows: ProjectAction[], groupBy: ActionGroupBy): ActionLane[] {
  if (groupBy === "assignee") {
    const named = new Map<string, ProjectAction[]>();
    const unassigned: ProjectAction[] = [];
    for (const a of rows) {
      const who = a.assignee?.trim();
      if (!who) { unassigned.push(a); continue; }
      if (!named.has(who)) named.set(who, []);
      named.get(who)!.push(a);
    }
    const lanes: ActionLane[] = [...named.keys()]
      .sort((x, y) => x.localeCompare(y))
      .map((k) => ({ key: k, label: k, rows: named.get(k)! }));
    // Named lanes always render (they're drop targets); Unassigned only when it has rows.
    if (unassigned.length) lanes.unshift({ key: null, label: "Unassigned", rows: unassigned });
    return lanes;
  }

  // clientStatus: canonical lane order, empty lanes kept as drop targets. Rows with no
  // clientStatus land in Unset rather than being asserted into "With Aeronet".
  const byLane = new Map<string, ProjectAction[]>();
  const unset: ProjectAction[] = [];
  for (const a of rows) {
    const s = a.clientStatus ? String(a.clientStatus) : "";
    if (!s) { unset.push(a); continue; }
    if (!byLane.has(s)) byLane.set(s, []);
    byLane.get(s)!.push(a);
  }
  const lanes: ActionLane[] = [{ key: null, label: "Unset", rows: unset }];
  for (const lane of CLIENT_STATUS_LANES) {
    lanes.push({ key: lane, label: lane, rows: byLane.get(lane) ?? [] });
  }
  // A value outside the canonical set (legacy/imported row) still gets a lane.
  for (const [k, v] of byLane) {
    if (!CLIENT_STATUS_LANES.includes(k as ClientStatus)) lanes.push({ key: k, label: k, rows: v });
  }
  return lanes;
}
